import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  RadioGroup, FormControlLabel, Radio, LinearProgress
} from '@mui/material';
import { MdPersonAddDisabled } from "react-icons/md";
import { BsFillPersonCheckFill } from "react-icons/bs";
import Swal from 'sweetalert2';

import {
  obtenerBibliotecariosPaginados,
  registrarBibliotecario,
  habilitarPersonal,
  inhabilitarPersonal
} from '../../../services/PersonalBibliotecarioService';

import { esCorreoValido } from '../../../utils/functions';
import PasswordFieldWithPolicy from '../passwordFieldWithPolicy/PasswordFieldWithPolicy'; // <<--- NUEVO (ajusta la ruta)
import { passwordPolicyCheck } from '../../../utils/passwordPolicy';       // <<--- NUEVO (ajusta la ruta)
import JumpByItemFooter from '../customTableFooter/JumpByItemFooter';
import './administracion.css';

export default function AdministracionBibliotecarios() {
  const [bibliotecarios, setBibliotecarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegistro, setLoadingRegistro] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [pagingMetadata, setPagingMetadata] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [errores, setErrores] = useState({
    nombreCompleto: '',
    correoInstitucional: '',
    password: ''
  });

  const [nuevoBibliotecario, setNuevoBibliotecario] = useState({
    nombreCompleto: '',
    correoInstitucional: '',
    password: '',
    sexo: 'M',
  });

  const fetchBibliotecarios = async () => {
    setLoading(true);
    try {
      const response = await obtenerBibliotecariosPaginados(page, pageSize);
      const { content, totalItems, totalPages, totalElements } = response.data;
      setBibliotecarios(content);
      setPagingMetadata({ totalElements: totalItems ?? totalElements, totalPages });
    } catch (error) {
      console.error('Error al obtener bibliotecarios', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBibliotecarios(); }, [page]);

  const handleOpenDialog = () => setOpenDialog(true);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNuevoBibliotecario({
      nombreCompleto: '',
      correoInstitucional: '',
      password: '',
      sexo: 'M',
    });
    setConfirmPassword('');
    setErrores({ nombreCompleto: '', correoInstitucional: '', password: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNuevoBibliotecario(prev => ({ ...prev, [name]: value }));
    // Limpia errores en tiempo real
    if (name in errores && errores[name]) {
      setErrores(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'password' && errores.password) {
      setErrores(prev => ({ ...prev, password: '' }));
    }
  };

  const handleRegistrarBibliotecario = async () => {
    const { nombreCompleto, correoInstitucional, password } = nuevoBibliotecario;
    let hayErrores = false;
    const nuevosErrores = { nombreCompleto: '', correoInstitucional: '', password: '' };

    if (!nombreCompleto) { nuevosErrores.nombreCompleto = 'Este campo es requerido'; hayErrores = true; }

    if (!correoInstitucional) {
      nuevosErrores.correoInstitucional = 'Este campo es requerido'; hayErrores = true;
    } else if (!esCorreoValido(correoInstitucional)) {
      nuevosErrores.correoInstitucional = 'Ingresa un correo electrónico válido'; hayErrores = true;
    }

    if (!password) {
      nuevosErrores.password = 'Este campo es requerido'; hayErrores = true;
    } else {
      const { valid } = passwordPolicyCheck(password, { nombreCompleto, correoInstitucional });
      if (!valid) {
        nuevosErrores.password = 'La contraseña no cumple con la política de seguridad.';
        hayErrores = true;
      }
    }

    if (!confirmPassword) {
      nuevosErrores.password = nuevosErrores.password || 'Confirma tu contraseña.';
      hayErrores = true;
    } else if (password && confirmPassword && password !== confirmPassword) {
      nuevosErrores.password = 'Las contraseñas no coinciden.';
      hayErrores = true;
    }

    setErrores(nuevosErrores);
    if (hayErrores) return;

    setLoadingRegistro(true);
    try {
      await registrarBibliotecario(nuevoBibliotecario);
      handleCloseDialog();
      Swal.fire({
        title: 'Éxito',
        text: 'El bibliotecario ha sido registrado con éxito.',
        icon: 'success',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
      fetchBibliotecarios();
    } catch (error) {
      const mensajeError =
        error?.response?.data && typeof error.response.data === 'object'
          ? error.response.data.message
          : error?.response?.data || 'No se pudo registrar el bibliotecario.';
      handleCloseDialog();
      Swal.fire({
        title: 'Error',
        text: mensajeError,
        icon: 'error',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
    } finally {
      setLoadingRegistro(false);
    }
  };

  const handleCambiarEstado = async (id, habilitar) => {
    try {
      const action = habilitar ? habilitarPersonal : inhabilitarPersonal;
      await action(id);
      Swal.fire({
        title: 'Éxito',
        text: `El bibliotecario ha sido ${habilitar ? 'habilitado' : 'deshabilitado'} con éxito.`,
        icon: 'success',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
      fetchBibliotecarios();
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: `No se pudo ${habilitar ? 'habilitar' : 'inhabilitar'} al bibliotecario.`,
        icon: 'error',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#21807C',
        customClass: { container: 'swal-overlay' },
      });
    }
  };

  const columnas = [
    { field: 'id', headerName: 'ID', width: 100, headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center' },
    {
      field: 'userPic',
      headerName: 'Foto de Perfil',
      sortable: false,
      width: 150,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'tablaVoluntarios-headers',
      renderCell: (params) => (
        <img src={params.value} alt="Foto" style={{ width: 71, height: 71, borderRadius: '50%', objectFit: 'cover' }} />
      ),
    },
    { field: 'nombreCompleto', headerName: 'Nombre', width: 250, headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center' },
    { field: 'correoInstitucional', headerName: 'Correo Institucional', width: 260, headerClassName: 'tablaVoluntarios-headers', align: 'center', headerAlign: 'center' },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'tablaVoluntarios-headers',
      renderCell: (params) => (
        <span
          style={{
            backgroundColor: params.row.estado ? 'rgb(145 254 159 / 80%)' : '#ffadad8f',
            color: params.row.estado ? 'green' : 'red',
            padding: '6px',
            borderRadius: 4,
          }}
        >
          {params.row.estado ? 'Habilitado' : 'Deshabilitado'}
        </span>
      ),
    },
    {
      field: 'acciones',
      headerName: 'Habilitar / Deshabilitar',
      sortable: false,
      width: 180,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'tablaVoluntarios-headers',
      renderCell: (params) => (
        <Button
          className={params.row.estado ? 'tableInventario-deleteBtn' : 'tableVoluntarios-habilitarBtn'}
          style={{ width: 40, height: 40 }}
          onClick={() => handleCambiarEstado(params.row.id, !params.row.estado)}
        >
          {params.row.estado ? <MdPersonAddDisabled color='white' size={22} /> : <BsFillPersonCheckFill color='white' size={22} />}
        </Button>
      ),
    },
  ];

  return (
    <div className='voluntarios-container'>
      <h1>Bibliotecarios</h1>

      <div className='voluntarios-top'>
        <button onClick={handleOpenDialog}>Registrar</button>
      </div>

      {/* Modal registrar bibliotecario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Registrar Bibliotecario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="nombreCompleto"
            label="Nombre Completo"
            type="text"
            fullWidth
            variant="outlined"
            value={nuevoBibliotecario.nombreCompleto}
            onChange={handleChange}
            error={!!errores.nombreCompleto}
            helperText={errores.nombreCompleto}
          />
          <TextField
            margin="dense"
            name="correoInstitucional"
            label="Correo Institucional"
            type="email"
            fullWidth
            variant="outlined"
            value={nuevoBibliotecario.correoInstitucional}
            onChange={handleChange}
            error={!!errores.correoInstitucional}
            helperText={errores.correoInstitucional}
          />

          {/* Campo reutilizable de contraseña + checklist + confirmar */}
          <PasswordFieldWithPolicy
            value={nuevoBibliotecario.password}
            onChange={handleChange}
            name="password"
            label="Contraseña"
            fullName={nuevoBibliotecario.nombreCompleto}
            email={nuevoBibliotecario.correoInstitucional}
            errorText={errores.password}
            confirmValue={confirmPassword}
            onConfirmChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errores.password) setErrores(prev => ({ ...prev, password: '' }));
            }}
            showConfirm
          />

          <RadioGroup row name="sexo" value={nuevoBibliotecario.sexo} onChange={handleChange}>
            <FormControlLabel value="M" control={<Radio />} label="Masculino" />
            <FormControlLabel value="F" control={<Radio />} label="Femenino" />
          </RadioGroup>

          {loadingRegistro && <LinearProgress color="success" />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegistrarBibliotecario} color="success" disabled={loadingRegistro}>Registrar</Button>
          <Button onClick={handleCloseDialog} color="error" disabled={loadingRegistro}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <div className='tablaVoluntariosContainer'>
        <DataGrid
          rows={bibliotecarios}
          columns={columnas}
          paginationMode="server"
          pagination
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          rowCount={pagingMetadata.totalElements}
          loading={loading}
          getRowId={(row) => row.id}
          disableSelectionOnClick
          rowHeight={80}
          hideFooterSelectedRowCount
          pageSizeOptions={[]}
          slots={{ footer: JumpByItemFooter }}
          slotProps={{
            footer: {
              total: pagingMetadata.totalElements ?? 0,
              page,
              pageSize,
              onJumpPage: (p) => setPage(p),
              noun: 'bibliotecario',
            },
          }}
        />
      </div>
    </div>
  );
}
